import { useState, useEffect, useRef, useMemo } from 'react'
import useSettingsStore from '../store/settingsStore'
import { ANIMATION_STATES } from '../constants/animationStates'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001'

// Cache for loaded spritesheet data
const spritesheetCache = new Map()

// Cache for uploaded spritesheet URLs (per broadcaster)
const uploadedSpritesheetCache = new Map()

// Fetch uploaded spritesheet URLs for a broadcaster/critter type
async function getUploadedSpritesheetUrls(broadcasterLogin, critterType) {
  if (!broadcasterLogin) {
    console.log('[AnimatedSprite] No broadcaster login, skipping uploaded spritesheet check')
    return null
  }

  const cacheKey = `${broadcasterLogin}-${critterType}`
  if (uploadedSpritesheetCache.has(cacheKey)) {
    console.log(`[AnimatedSprite] Using cached data for ${cacheKey}`)
    return uploadedSpritesheetCache.get(cacheKey)
  }

  try {
    const url = `${API_BASE}/twitch/api/broadcaster/${broadcasterLogin}/spritesheets/${critterType}`
    console.log(`[AnimatedSprite] Fetching uploaded spritesheets from: ${url}`)
    const response = await fetch(url)

    if (!response.ok) {
      console.log(`[AnimatedSprite] No uploaded spritesheets found for ${critterType} (status: ${response.status})`)
      return null
    }

    const data = await response.json()
    console.log(`[AnimatedSprite] Found uploaded spritesheets for ${critterType}:`, data)
    uploadedSpritesheetCache.set(cacheKey, data)
    return data
  } catch (error) {
    console.error('[AnimatedSprite] Error fetching uploaded spritesheets:', error)
    return null
  }
}

// Load spritesheet JSON data from a URL
async function loadSpritesheetFromUrl(jsonUrl, pngUrl) {
  try {
    // Handle relative URLs by prepending API_BASE
    const fullJsonUrl = jsonUrl.startsWith('/') ? `${API_BASE}${jsonUrl}` : jsonUrl
    const fullPngUrl = pngUrl.startsWith('/') ? `${API_BASE}${pngUrl}` : pngUrl

    const response = await fetch(fullJsonUrl)
    if (!response.ok) return null

    const data = await response.json()

    // Parse frame data into an array sorted by frame number
    const frames = Object.entries(data.frames)
      .map(([name, frameData]) => ({
        name,
        ...frameData.frame
      }))
      .sort((a, b) => {
        const numA = parseInt(a.name.match(/\d+/)?.[0] || '0')
        const numB = parseInt(b.name.match(/\d+/)?.[0] || '0')
        return numA - numB
      })

    return {
      frames,
      imagePath: fullPngUrl,
      frameWidth: frames[0]?.w || 600,
      frameHeight: frames[0]?.h || 600,
      totalFrames: frames.length
    }
  } catch (error) {
    console.error('Error loading spritesheet from URL:', error)
    return null
  }
}

// Load spritesheet JSON data from default assets
async function loadDefaultSpritesheet(critterType, animationState) {
  const cacheKey = `default-${critterType}-${animationState}`

  if (spritesheetCache.has(cacheKey)) {
    return spritesheetCache.get(cacheKey)
  }

  try {
    // Try to load the JSON file for this animation
    const jsonPath = `/assets/animations/${critterType} spritesheet - ${animationState}.json`
    const response = await fetch(jsonPath)

    if (!response.ok) {
      return null
    }

    const data = await response.json()

    // Parse frame data into an array sorted by frame number
    const frames = Object.entries(data.frames)
      .map(([name, frameData]) => ({
        name,
        ...frameData.frame
      }))
      .sort((a, b) => {
        const numA = parseInt(a.name.match(/\d+/)?.[0] || '0')
        const numB = parseInt(b.name.match(/\d+/)?.[0] || '0')
        return numA - numB
      })

    // Get the spritesheet image path
    const pngPath = `/assets/animations/${critterType}_spritesheet_-_${animationState}.png`

    const spritesheetData = {
      frames,
      imagePath: pngPath,
      frameWidth: frames[0]?.w || 600,
      frameHeight: frames[0]?.h || 600,
      totalFrames: frames.length
    }

    spritesheetCache.set(cacheKey, spritesheetData)
    return spritesheetData
  } catch (error) {
    return null
  }
}

// Load spritesheet data (uploaded first, then default)
async function loadSpritesheetData(broadcasterLogin, critterType, animationState) {
  const cacheKey = `${broadcasterLogin || 'default'}-${critterType}-${animationState}`
  console.log(`[AnimatedSprite] Loading spritesheet: ${cacheKey}`)

  if (spritesheetCache.has(cacheKey)) {
    console.log(`[AnimatedSprite] Using cached spritesheet for ${cacheKey}`)
    return spritesheetCache.get(cacheKey)
  }

  // Try uploaded spritesheets first
  if (broadcasterLogin) {
    const uploadedUrls = await getUploadedSpritesheetUrls(broadcasterLogin, critterType)
    if (uploadedUrls && uploadedUrls[animationState]) {
      console.log(`[AnimatedSprite] Found uploaded URLs for ${animationState}:`, uploadedUrls[animationState])
      const { pngUrl, jsonUrl } = uploadedUrls[animationState]
      const data = await loadSpritesheetFromUrl(jsonUrl, pngUrl)
      if (data) {
        console.log(`[AnimatedSprite] Successfully loaded uploaded spritesheet for ${cacheKey}`)
        spritesheetCache.set(cacheKey, data)
        return data
      } else {
        console.log(`[AnimatedSprite] Failed to load uploaded spritesheet data`)
      }
    } else {
      console.log(`[AnimatedSprite] No uploaded spritesheet for animation state: ${animationState}`)
    }
  }

  // Fall back to default assets
  console.log(`[AnimatedSprite] Trying default spritesheet for ${critterType}/${animationState}`)
  const defaultData = await loadDefaultSpritesheet(critterType, animationState)
  if (defaultData) {
    console.log(`[AnimatedSprite] Loaded default spritesheet for ${cacheKey}`)
    spritesheetCache.set(cacheKey, defaultData)
  } else {
    console.log(`[AnimatedSprite] No default spritesheet found for ${critterType}/${animationState}`)
  }
  return defaultData
}

function AnimatedSprite({
  critterType,
  isWalking = false,
  isFeeding = false,
  isPetting = false,
  facingLeft = false,
  className = '',
  broadcasterLogin = null
}) {
  const [spritesheetData, setSpritesheetData] = useState(null)
  const [currentFrame, setCurrentFrame] = useState(0)
  const [imageLoaded, setImageLoaded] = useState(false)
  const animationRef = useRef(null)
  const imageRef = useRef(null)

  const animationFps = useSettingsStore((state) => state.settings.animationFps)

  // Determine current animation state
  const animationState = useMemo(() => {
    if (isFeeding) return ANIMATION_STATES.eat
    if (isPetting) return ANIMATION_STATES.pet
    if (isWalking) return ANIMATION_STATES.walk
    return ANIMATION_STATES.idle
  }, [isWalking, isFeeding, isPetting])

  // Load spritesheet data when critter type or animation state changes
  useEffect(() => {
    let mounted = true

    async function loadData() {
      // First try to load the specific animation
      let data = await loadSpritesheetData(broadcasterLogin, critterType, animationState)

      // If specific animation not found, fall back to idle
      if (!data && animationState !== ANIMATION_STATES.idle) {
        data = await loadSpritesheetData(broadcasterLogin, critterType, ANIMATION_STATES.idle)
      }

      if (mounted && data) {
        setSpritesheetData(data)
        setCurrentFrame(0)
        setImageLoaded(false)
      }
    }

    loadData()

    return () => {
      mounted = false
    }
  }, [broadcasterLogin, critterType, animationState])

  // Preload image when spritesheet data changes
  useEffect(() => {
    if (!spritesheetData) return

    const img = new Image()
    img.onload = () => setImageLoaded(true)
    img.onerror = () => console.error(`Failed to load spritesheet image: ${spritesheetData.imagePath}`)
    img.src = spritesheetData.imagePath
    imageRef.current = img
  }, [spritesheetData])

  // Animation loop
  useEffect(() => {
    if (!spritesheetData || !imageLoaded) return

    const frameInterval = 1000 / animationFps

    animationRef.current = setInterval(() => {
      setCurrentFrame(prev => (prev + 1) % spritesheetData.totalFrames)
    }, frameInterval)

    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current)
      }
    }
  }, [spritesheetData, imageLoaded, animationFps])

  // If no spritesheet data, return nothing (Critter will fall back to static image)
  if (!spritesheetData || !imageLoaded) {
    return null
  }

  const frame = spritesheetData.frames[currentFrame]
  if (!frame) return null

  // Target display size - matches the critter-sprite CSS clamp
  const displaySize = 120 // pixels - middle of clamp(80px, 10vw, 150px)

  // Calculate scale ratio from native frame size to display size
  const scale = displaySize / frame.w

  // Scale the background position and get the total spritesheet dimensions
  const scaledBgX = frame.x * scale
  const scaledBgY = frame.y * scale

  // Calculate scaled spritesheet size (we need to know total spritesheet dimensions)
  // The spritesheet image is loaded in imageRef, we can get its dimensions
  const imgWidth = imageRef.current?.naturalWidth || frame.w
  const imgHeight = imageRef.current?.naturalHeight || frame.h
  const scaledBgWidth = imgWidth * scale
  const scaledBgHeight = imgHeight * scale

  return (
    <div
      className={`animated-sprite ${className}`}
      style={{
        width: displaySize,
        height: displaySize,
        backgroundImage: `url(${spritesheetData.imagePath})`,
        backgroundPosition: `-${scaledBgX}px -${scaledBgY}px`,
        backgroundSize: `${scaledBgWidth}px ${scaledBgHeight}px`,
        backgroundRepeat: 'no-repeat',
        transform: facingLeft ? 'scaleX(-1)' : 'scaleX(1)',
        imageRendering: 'pixelated'
      }}
    />
  )
}

// Check if animated spritesheet exists for a critter type
export async function hasAnimatedSprite(broadcasterLogin, critterType) {
  const data = await loadSpritesheetData(broadcasterLogin, critterType, ANIMATION_STATES.idle)
  return data !== null
}

// Preload all animations for a critter type
export async function preloadAnimations(broadcasterLogin, critterType) {
  const promises = Object.values(ANIMATION_STATES).map(state =>
    loadSpritesheetData(broadcasterLogin, critterType, state)
  )
  await Promise.all(promises)
}

// Clear cache for a broadcaster (call after uploading new spritesheets)
export function clearSpritesheetCache(broadcasterLogin, critterType = null) {
  if (critterType) {
    // Clear specific critter type
    const prefix = `${broadcasterLogin}-${critterType}`
    for (const key of spritesheetCache.keys()) {
      if (key.startsWith(prefix)) {
        spritesheetCache.delete(key)
      }
    }
    uploadedSpritesheetCache.delete(`${broadcasterLogin}-${critterType}`)
  } else {
    // Clear all for broadcaster
    for (const key of spritesheetCache.keys()) {
      if (key.startsWith(broadcasterLogin)) {
        spritesheetCache.delete(key)
      }
    }
    for (const key of uploadedSpritesheetCache.keys()) {
      if (key.startsWith(broadcasterLogin)) {
        uploadedSpritesheetCache.delete(key)
      }
    }
  }
}

export default AnimatedSprite
